import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useAxios } from '@/hooks/useAxios';
import {
  buildToolTimeline,
  extractRecorderResult,
  type RecorderResult,
  type ToolEvent,
} from '@/sections/asset/components/tabs/credentials/RecorderProgress/buildTimeline';
import type { Message } from '@/types';

interface RecorderProgressProps {
  conversationId: string;
  label: string;
  // Fired once when the agent's `finalize` tool response arrives. Caller is
  // responsible for showing the terminal state UI (Close button, retry, etc.).
  onComplete?: (result: RecorderResult) => void;
}

const POLL_INTERVAL_MS = 3_000;

export const RecorderProgress: React.FC<RecorderProgressProps> = ({
  conversationId,
  label,
  onComplete,
}) => {
  const messages = useRecorderMessages(conversationId);

  const events = useMemo(() => buildToolTimeline(messages), [messages]);
  const finalize = events.find(
    e => e.name === 'finalize' && e.status !== 'running'
  );
  const result = finalize ? extractRecorderResult(finalize) : null;

  // Fire onComplete exactly once when finalize lands.
  const completedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!finalize || completedRef.current === finalize.id) return;
    completedRef.current = finalize.id;
    const finalResult = extractRecorderResult(finalize);
    if (finalResult) onComplete?.(finalResult);
  }, [finalize, onComplete]);

  // Auto-scroll the inner list to the bottom as new events arrive so the
  // current step is always visible without expanding the modal.
  const listRef = useRef<HTMLOListElement>(null);
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [events.length]);

  return (
    <div className="space-y-4">
      <Header label={label} result={result} />
      <ol
        ref={listRef}
        className="border-chariot-stroke divide-chariot-stroke max-h-[55vh] divide-y overflow-y-auto rounded-lg border"
      >
        {events.length === 0 ? (
          <li className="text-chariot-text-secondary flex items-center gap-2 p-3 text-sm">
            <Spinner />
            Waiting for the recorder to take its first step...
          </li>
        ) : (
          events.map(e => <ToolRow key={e.id} event={e} />)
        )}
      </ol>
    </div>
  );
};

const Header: React.FC<{ label: string; result: RecorderResult | null }> = ({
  label,
  result,
}) => {
  let subtitle =
    'The recorder is driving a browser through your login flow. Each step appears below as it runs.';
  if (result?.committed) {
    subtitle = `Credential "${result.label ?? label}" committed. Closing in a moment...`;
  } else if (result && !result.committed) {
    subtitle = result.blockers?.length
      ? `Recorder finished without committing: ${result.blockers.join('; ')}`
      : (result.rationale ??
        'Recorder finished without committing a credential.');
  }
  return (
    <div>
      <h3 className="text-chariot-text-primary text-base font-semibold">
        Recording login for {label}
      </h3>
      <p className="text-chariot-text-secondary text-sm">{subtitle}</p>
    </div>
  );
};

const ToolRow: React.FC<{ event: ToolEvent }> = ({ event }) => {
  const isTerminal = event.name === 'finalize' && event.status !== 'running';
  const committed = isTerminal && event.response?.committed === true;
  const terminalClass = !isTerminal
    ? ''
    : committed
      ? 'bg-chariot-status-green/5'
      : 'bg-chariot-status-red/5';

  return (
    <li className={`flex items-start gap-3 p-3 ${terminalClass}`}>
      <StatusIcon status={event.status} />
      <div className="min-w-0 flex-1">
        <div className="text-chariot-text-primary truncate text-sm font-medium">
          {isTerminal
            ? committed
              ? 'Recorder finished — login recipe saved'
              : 'Recorder finished — could not record login'
            : event.prettyName}
        </div>
        {event.description && (
          <div
            className={`text-chariot-text-secondary truncate text-xs ${isTerminal ? '' : 'font-mono'}`}
          >
            {event.description}
          </div>
        )}
        {event.errorText && (
          <div className="text-chariot-status-red mt-1 text-xs">
            {event.errorText}
          </div>
        )}
      </div>
    </li>
  );
};

const StatusIcon: React.FC<{ status: ToolEvent['status'] }> = ({ status }) => {
  switch (status) {
    case 'running':
      return <Spinner />;
    case 'success':
      return (
        <CheckCircleIcon className="text-chariot-status-green size-5 shrink-0" />
      );
    case 'error':
      return (
        <XCircleIcon className="text-chariot-status-red size-5 shrink-0" />
      );
    default:
      return (
        <ExclamationTriangleIcon className="text-chariot-text-secondary size-5 shrink-0" />
      );
  }
};

const Spinner: React.FC = () => (
  <span
    className="border-chariot-brand-primary inline-block size-5 shrink-0 animate-spin rounded-full border-2 border-t-transparent"
    role="status"
    aria-label="In progress"
  />
);

// Recorder conversations are created with WithPublic(aws.Username) so messages
// land on the tenant partition, not the per-user composite partition. We bypass
// useChatWorker (which hardcodes user=true) and fetch the tenant partition
// directly. Polls every 3s — VITE_WS_URL is unset in dev so WS-driven updates
// don't fire; polling is the only reliable path.
function useRecorderMessages(conversationId: string): Message[] {
  const axios = useAxios();
  const [messages, setMessages] = useState<Message[]>([]);

  useEffect(() => {
    if (!conversationId) return;
    let cancelled = false;

    const fetchOnce = async () => {
      try {
        const response = await axios.get('/my', {
          params: { label: 'message', key: `#message#${conversationId}` },
        });
        if (cancelled) return;
        const list = (response.data.messages || []) as Message[];
        setMessages(prev => (sameMessages(prev, list) ? prev : list));
      } catch (err) {
        console.error('RecorderProgress: fetch messages failed', err);
      }
    };

    void fetchOnce();
    const interval = setInterval(fetchOnce, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [conversationId, axios]);

  return messages;
}

// Stable-identity check: skip the state update (and downstream re-renders) when
// the polled list matches what we already have.
function sameMessages(a: Message[], b: Message[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].key !== b[i].key) return false;
  }
  return true;
}
